import React, { useEffect, useState } from "react";
import { Target, Sparkles, Check } from "lucide-react";
import api from "../lib/api.js";
import { goalConfetti, chime } from "../lib/celebrate.js";

/**
 * Today's Coach-issued mission. Pulls from /api/daily/mission, shows progress
 * vs target, and offers a Claim button once complete.
 */
export default function DailyMissionCard({ onClaim }) {
  const [data, setData] = useState(null);
  const [claiming, setClaiming] = useState(false);

  const load = () =>
    api.get("/daily/mission").then((r) => setData(r.data)).catch(() => {});

  useEffect(() => {
    load();
  }, []);

  if (!data) {
    return (
      <div className="ps-card p-5 text-slate-400 text-sm ps-serif ps-italic">
        Loading today's mission…
      </div>
    );
  }
  const { mission, progress, completed, claimed } = data;
  const pct = Math.min(100, (progress / Math.max(1, mission.target)) * 100);

  const claim = async () => {
    if (claiming || claimed) return;
    setClaiming(true);
    try {
      await api.post("/daily/mission/claim");
      goalConfetti();
      chime();
      await load();
      onClaim?.(mission.reward);
    } finally {
      setClaiming(false);
    }
  };

  return (
    <div className="ps-card p-5 relative overflow-hidden" data-testid="daily-mission-card">
      <div className="absolute -top-10 -right-10 w-44 h-44 rounded-full bg-[#FBBF24]/15 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-12 -left-12 w-40 h-40 rounded-full bg-[#a78bfa]/10 blur-3xl pointer-events-none" />
      <div className="relative flex items-start gap-3">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#FBBF24]/30 to-[#ef4444]/20 border border-[#FBBF24]/30 flex items-center justify-center shrink-0">
          <Target size={22} color="#FBBF24" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold text-[#FBBF24]">
            <Sparkles size={11} /> Today's mission
          </div>
          <div className="ps-heading text-lg font-extrabold mt-1 truncate" data-testid="mission-title">
            {mission.title}
          </div>
          <div className="text-xs text-slate-400 leading-snug mt-1 ps-serif">
            {mission.description}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Reward</div>
          <div className="ps-heading text-xl font-extrabold ps-gradient-text ps-tabular">+{mission.reward}</div>
        </div>
      </div>

      {/* progress */}
      <div className="relative mt-4">
        <div className="flex items-center justify-between text-[10px] mb-1.5">
          <span className="text-slate-400 ps-serif ps-italic">
            {progress.toLocaleString()} <span className="text-slate-600">/ {mission.target.toLocaleString()}</span>
          </span>
          <span className="text-slate-300 font-bold ps-tabular" data-testid="mission-pct">{Math.round(pct)}%</span>
        </div>
        <div className="h-2 rounded-full bg-[#0E1F2A] overflow-hidden">
          <div
            className="h-full rounded-full bg-gradient-to-r from-[#FBBF24] via-[#ef4444] to-[#a855f7] transition-all duration-700"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      {/* CTA */}
      <div className="relative mt-4">
        {claimed ? (
          <div className="flex items-center gap-2 text-[#4ade80] text-sm font-bold">
            <Check size={16} /> Reward claimed
          </div>
        ) : completed ? (
          <button
            onClick={claim}
            disabled={claiming}
            className="ps-button-primary w-full disabled:opacity-50"
            data-testid="mission-claim-btn"
          >
            {claiming ? "Claiming…" : `Claim +${mission.reward} points`}
          </button>
        ) : (
          <div className="text-[11px] text-slate-500 ps-serif ps-italic">
            Keep going — finish today to lock in the bonus.
          </div>
        )}
      </div>
    </div>
  );
}
